import { postForm } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { inputValueToUnix, unixToInputValue } from '@/shared/lib/datetime';
import { normalizeOptions } from '@/shared/lib/normalizeOptions';
import { getListRows } from '@/shared/lib/pagination';
import type { ListResponse } from '@/shared/ui/list/types';
import type { SelectOption } from '@/shared/ui/selectTypes';

export interface NotificationCategory {
  id: number;
  description: string;
  parent_id?: number | null;
}

export async function fetchNotificationCategories() {
  const res = await postForm<{ categories?: NotificationCategory[] }>(
    '/notification/get-categories/',
    {},
  );
  return res.categories ?? [];
}

// ---- create / edit ---------------------------------------------------------

export const NOTIFICATION_TYPE = { ALL_CLIENTS: '1', ALL_SERVICES: '2', SELECTED_USERS: '3' } as const;

export type MobilePlatform = 'ios' | 'android';

export interface NotificationValues {
  categoryId: string;
  typeId: string;
  platforms: MobilePlatform[];
  message: string;
  /** `datetime-local` value. */
  sendTime: string;
  countryId: string;
  regionId: string;
  cities: SelectOption[];
  marks: SelectOption[];
  users: SelectOption[];
}

export const EMPTY_NOTIFICATION: NotificationValues = {
  categoryId: '',
  typeId: '',
  platforms: [],
  message: '',
  sendTime: '',
  countryId: '',
  regionId: '',
  cities: [],
  marks: [],
  users: [],
};

interface NamedRef {
  id: number | string;
  name: string;
}

export interface NotificationDetails {
  category_id?: number | string;
  type_id?: number | string;
  message?: string;
  send_time?: number;
  push_mobile_platform?: string;
  user_ids?: NamedRef[] | null;
  car_mark_ids?: NamedRef[] | null;
  country_region_ids?: {
    country?: { id?: number | string } | null;
    region?: Array<{ id?: number | string }> | null;
    city?: NamedRef[] | null;
  } | null;
}

const refsToOptions = (refs: NamedRef[] | null | undefined): SelectOption[] =>
  (refs ?? []).map(({ id, name }) => ({ value: String(id), label: name }));

const toPlatforms = (platform: string | undefined): MobilePlatform[] => {
  if (platform === 'all') return ['ios', 'android'];
  return platform === 'ios' || platform === 'android' ? [platform] : [];
};

export const notificationToValues = (item: NotificationDetails): NotificationValues => {
  const place = item.country_region_ids;
  return {
    categoryId: String(item.category_id ?? ''),
    typeId: String(item.type_id ?? ''),
    platforms: toPlatforms(item.push_mobile_platform),
    message: item.message ?? '',
    sendTime: unixToInputValue(item.send_time),
    countryId: String(place?.country?.id ?? ''),
    regionId: String(place?.region?.[0]?.id ?? ''),
    cities: refsToOptions(place?.city),
    marks: refsToOptions(item.car_mark_ids),
    users: refsToOptions(item.user_ids),
  };
};

export async function fetchNotification(id: number) {
  const res = await postForm<{ notification?: NotificationDetails }>('/notification/view/', { id });
  return res.notification;
}

export const saveNotification = (id: number | undefined, v: NotificationValues) => {
  const isForUsers = v.typeId === NOTIFICATION_TYPE.SELECTED_USERS;
  return submitForm(`/notification/${id ? 'edit' : 'create'}/`, {
    id,
    category_id: v.categoryId,
    type_id: v.typeId,
    message: v.message,
    send_time: inputValueToUnix(v.sendTime),
    push_mobile_platform: v.platforms.length > 1 ? 'all' : v.platforms[0],
    user_ids: isForUsers ? v.users.map((user) => user.value) : [],
    'country_region_ids[0][country_id]': v.countryId,
    'country_region_ids[0][region_id]': v.regionId ? [v.regionId] : [],
    'country_region_ids[0][city_id]': v.cities.map((city) => city.value),
    car_mark_ids: v.marks.map((mark) => mark.value),
  });
};

// ---- pickers ---------------------------------------------------------------

export async function fetchRegionOptions(countryId: string) {
  const res = await postForm<ListResponse>('/place/region/list/', { country_id: countryId });
  return normalizeOptions(getListRows(res, ['regions']));
}

export async function searchCities(countryId: string, regionId: string, name: string) {
  const res = await postForm<ListResponse>('/place/city/list/', {
    country_id: countryId,
    region_id: regionId,
    name,
  });
  return normalizeOptions(getListRows(res, ['cities']));
}

export async function searchCarMarks(name: string) {
  const res = await postForm<ListResponse>('/car/mark/list/', { name });
  return normalizeOptions(getListRows(res, ['marks']));
}

export async function searchUsers(countryId: string, regionId: string, name: string) {
  const res = await postForm<ListResponse>('/user/profile/list/', {
    name,
    country_id: countryId,
    region_id: regionId,
  });
  return normalizeOptions(getListRows(res, ['users']));
}
